import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Package,
  Plus,
  Pencil,
  Trash2,
  Search,
  Calculator,
  Euro,
  Percent,
  Box,
  Building2,
  Tag,
  Calendar,
  ScanBarcode
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import BarcodeScanner from "../components/BarcodeScanner";

// Supplier price management component
const SupplierPricesDialog = ({ product, suppliers, onClose }) => {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPrice, setNewPrice] = useState({
    supplier_id: "",
    article_number: "",
    package_size: product?.package_size || 1,
    package_price_net: 0,
    package_price_gross: 0
  });

  useEffect(() => {
    if (product) fetchPrices();
  }, [product]);

  const fetchPrices = async () => {
    try {
      const res = await axios.get(`${API}/product-supplier-prices/product/${product.id}`);
      setPrices(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!newPrice.supplier_id) {
      toast.error("Bitte Lieferant wählen");
      return;
    }
    try {
      await axios.post(`${API}/product-supplier-prices`, {
        product_id: product.id,
        ...newPrice
      });
      toast.success("Lieferantenpreis gespeichert");
      fetchPrices();
      setNewPrice({
        supplier_id: "",
        article_number: "",
        package_size: product?.package_size || 1,
        package_price_net: 0,
        package_price_gross: 0
      });
    } catch (err) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (priceId) => {
    try {
      await axios.delete(`${API}/product-supplier-prices/${priceId}`);
      toast.success("Gelöscht");
      fetchPrices();
    } catch (err) {
      toast.error("Fehler");
    }
  };

  return (
    <Dialog open={!!product} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Lieferantenpreise: {product?.name}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Existing prices */}
          {prices.length > 0 && (
            <div className="space-y-2">
              <Label>Aktuelle Lieferanten</Label>
              {prices.map((p, idx) => (
                <div key={p.id} className={`flex items-center gap-3 p-3 rounded-lg border ${idx === 0 ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200'}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{p.supplier_name}</span>
                      {idx === 0 && <Badge className="bg-emerald-500">Günstigster</Badge>}
                    </div>
                    <div className="text-sm text-slate-500 flex flex-wrap gap-x-4">
                      <span>Art.-Nr.: {p.article_number || "-"}</span>
                      <span>VPE: {p.package_size} Stk</span>
                      <span className="font-mono">{p.package_price_net?.toFixed(2)}€ netto</span>
                    </div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => handleDelete(p.id)}>
                    <Trash2 className="w-4 h-4 text-rose-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Add new supplier price */}
          <div className="border-t pt-4">
            <Label className="mb-3 block">Neuen Lieferanten hinzufügen</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Lieferant *</Label>
                <Select value={newPrice.supplier_id} onValueChange={(v) => setNewPrice({ ...newPrice, supplier_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.filter(s => !prices.find(p => p.supplier_id === s.id)).map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Artikelnummer</Label>
                <Input
                  value={newPrice.article_number}
                  onChange={(e) => setNewPrice({ ...newPrice, article_number: e.target.value })}
                  placeholder="z.B. 123456"
                />
              </div>
              <div>
                <Label className="text-xs">VPE-Größe</Label>
                <Input
                  type="number"
                  value={newPrice.package_size}
                  onChange={(e) => setNewPrice({ ...newPrice, package_size: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div>
                <Label className="text-xs">VPE-Preis Netto (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newPrice.package_price_net}
                  onChange={(e) => setNewPrice({ ...newPrice, package_price_net: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label className="text-xs">VPE-Preis Brutto (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newPrice.package_price_gross}
                  onChange={(e) => setNewPrice({ ...newPrice, package_price_gross: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <Button onClick={handleSave} className="mt-3 bg-rose-600 hover:bg-rose-700">
              <Plus className="w-4 h-4 mr-2" />
              Hinzufügen
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ProductCard = ({ product, onEdit, onDelete, onSupplierPrices }) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
    >
      <Card className="border-slate-200 hover:shadow-md transition-shadow overflow-hidden group">
        <div className="aspect-video bg-slate-100 relative overflow-hidden">
          {product.image_url ? (
            <img 
              src={product.image_url} 
              alt={product.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="w-12 h-12 text-slate-300" />
            </div>
          )}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
            <Button 
              size="icon" 
              variant="secondary"
              onClick={() => onEdit(product)}
              data-testid={`edit-product-${product.id}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button 
              size="icon" 
              variant="destructive"
              onClick={() => onDelete(product)}
              data-testid={`delete-product-${product.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <CardContent className="p-4">
          <h3 className="font-medium text-slate-900 truncate">{product.name}</h3>
          <div className="flex items-center justify-between mt-2">
            <span className="text-lg font-heading font-bold text-[#DC2626]">
              {product.price?.toFixed(2) || '0.00'}€
            </span>
            {product.category && (
              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                {product.category}
              </span>
            )}
          </div>
          <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-500 space-y-1">
            <div className="flex justify-between">
              <span>EK Netto:</span>
              <span className="font-mono">{product.purchase_price_net?.toFixed(4) || '0.0000'}€</span>
            </div>
            <div className="flex justify-between">
              <span>VPE:</span>
              <span className="font-mono">{product.package_size || 1} Stk</span>
            </div>
            {product.deposit > 0 && (
              <div className="flex justify-between text-amber-600">
                <span>Pfand:</span>
                <span className="font-mono">{product.deposit?.toFixed(2)}€</span>
              </div>
            )}
          </div>
          {/* Supplier Prices Button */}
          <Button
            variant="outline"
            size="sm"
            className="w-full mt-3 text-xs"
            onClick={() => onSupplierPrices(product)}
          >
            <Building2 className="w-3 h-3 mr-1" />
            Lieferantenpreise
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const ProductForm = ({ product, onSave, onClose }) => {
  const [name, setName] = useState(product?.name || "");
  const [category, setCategory] = useState(product?.category || "");
  const [imageUrl, setImageUrl] = useState(product?.image_url || "");
  const [ean, setEan] = useState(product?.ean || "");
  const [packagePriceNet, setPackagePriceNet] = useState(product?.package_price_net?.toString() || "");
  const [packageSize, setPackageSize] = useState(product?.package_size?.toString() || "1");
  const [vatRate, setVatRate] = useState(product?.vat_rate?.toString() || "19");
  const [markupPercent, setMarkupPercent] = useState(product?.markup_percent?.toString() || "30");
  const [deposit, setDeposit] = useState(product?.deposit?.toString() || "0");
  const [minStock, setMinStock] = useState(product?.min_stock?.toString() || "10");
  const [targetStock, setTargetStock] = useState(product?.target_stock?.toString() || "50");
  const [expiryDate, setExpiryDate] = useState(product?.expiry_date?.split('T')[0] || "");
  const [saving, setSaving] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  // Berechnete Werte
  const calcPackagePriceGross = () => {
    const net = parseFloat(packagePriceNet) || 0;
    const vat = parseFloat(vatRate) || 19;
    return net * (1 + vat / 100);
  };

  const calcPurchasePriceNet = () => {
    const net = parseFloat(packagePriceNet) || 0;
    const size = parseInt(packageSize) || 1;
    return net / size;
  };

  const calcPurchasePriceGross = () => {
    return calcPackagePriceGross() / (parseInt(packageSize) || 1);
  };

  const calcSalePrice = () => {
    const purchaseGross = calcPurchasePriceGross();
    const markup = parseFloat(markupPercent) || 0;
    const dep = parseFloat(deposit) || 0;
    return purchaseGross * (1 + markup / 100) + dep;
  };

  const handleBarcodeScan = (code) => {
    setEan(code);
    toast.success(`Barcode gescannt: ${code}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name ist erforderlich");
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        category: category.trim(),
        image_url: imageUrl.trim(),
        ean: ean.trim(),
        package_price_net: parseFloat(packagePriceNet) || 0,
        package_size: parseInt(packageSize) || 1,
        vat_rate: parseFloat(vatRate) || 19,
        markup_percent: parseFloat(markupPercent) || 30,
        deposit: parseFloat(deposit) || 0,
        min_stock: parseInt(minStock) || 10,
        target_stock: parseInt(targetStock) || 50,
        expiry_date: expiryDate || null
      };

      if (product) {
        await axios.put(`${API}/products/${product.id}`, data);
        toast.success("Produkt aktualisiert");
      } else {
        await axios.post(`${API}/products`, data);
        toast.success("Produkt erstellt");
      }
      onClose();
      setTimeout(() => onSave(), 100);
    } catch (err) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="basic">Basis</TabsTrigger>
          <TabsTrigger value="purchase">Einkauf</TabsTrigger>
          <TabsTrigger value="sale">Verkauf</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>Produktname *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Mars Riegel"
              data-testid="product-name-input"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label>Kategorie</Label>
            <Input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="z.B. Süßigkeiten, Getränke"
              data-testid="product-category-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Bild-URL</Label>
            <Input
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://..."
              data-testid="product-image-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <ScanBarcode className="w-4 h-4" />
              EAN / Barcode
            </Label>
            <div className="flex gap-2">
              <Input
                value={ean}
                onChange={(e) => setEan(e.target.value)}
                placeholder="z.B. 4001234567890"
                data-testid="product-ean-input"
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setScannerOpen(true)}
                data-testid="product-scan-btn"
              >
                <ScanBarcode className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Mindesthaltbarkeitsdatum (MHD)
            </Label>
            <Input
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              data-testid="product-expiry-date-input"
            />
            <p className="text-xs text-slate-500">
              Warnung: 30 Tage (gelb), 14 Tage (orange), abgelaufen (rot)
            </p>
          </div>
        </TabsContent>

        <TabsContent value="purchase" className="space-y-4 pt-4">
          <div className="bg-slate-50 rounded-md p-4 space-y-4">
            <h4 className="font-medium text-slate-700 flex items-center gap-2">
              <Box className="w-4 h-4" />
              Verpackungseinheit (VPE)
            </h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Stück pro Verpackung</Label>
                <Input
                  type="number"
                  min="1"
                  value={packageSize}
                  onChange={(e) => setPackageSize(e.target.value)}
                  placeholder="z.B. 24"
                  data-testid="product-package-size-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Preis/VPE Netto (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={packagePriceNet}
                  onChange={(e) => setPackagePriceNet(e.target.value)}
                  placeholder="z.B. 12.50"
                  data-testid="product-package-price-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>MwSt-Satz (%)</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                value={vatRate}
                onChange={(e) => setVatRate(e.target.value)}
                placeholder="19"
                data-testid="product-vat-input"
              />
            </div>
          </div>

          {/* Berechnete Werte */}
          <div className="bg-blue-50 rounded-md p-4 space-y-2">
            <h4 className="font-medium text-blue-700 flex items-center gap-2">
              <Calculator className="w-4 h-4" />
              Berechnete Einkaufspreise
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-blue-600">VPE Brutto:</span>
                <span className="font-mono font-bold ml-2">{calcPackagePriceGross().toFixed(2)}€</span>
              </div>
              <div>
                <span className="text-blue-600">Stück Netto:</span>
                <span className="font-mono font-bold ml-2">{calcPurchasePriceNet().toFixed(4)}€</span>
              </div>
              <div>
                <span className="text-blue-600">Stück Brutto:</span>
                <span className="font-mono font-bold ml-2">{calcPurchasePriceGross().toFixed(4)}€</span>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="sale" className="space-y-4 pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Percent className="w-3 h-3" />
                Aufschlag (%)
              </Label>
              <Input
                type="number"
                step="1"
                min="0"
                value={markupPercent}
                onChange={(e) => setMarkupPercent(e.target.value)}
                placeholder="30"
                data-testid="product-markup-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Euro className="w-3 h-3" />
                Pfand (€)
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={deposit}
                onChange={(e) => setDeposit(e.target.value)}
                placeholder="0.25"
                data-testid="product-deposit-input"
              />
            </div>
          </div>

          {/* Bestandsmanagement */}
          <div className="bg-slate-50 rounded-md p-4 space-y-4">
            <h4 className="font-medium text-slate-700 flex items-center gap-2">
              <Box className="w-4 h-4" />
              Bestandsmanagement
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mindestbestand (Stück)</Label>
                <Input
                  type="number"
                  min="0"
                  value={minStock}
                  onChange={(e) => setMinStock(e.target.value)}
                  placeholder="10"
                  data-testid="product-min-stock-input"
                />
                <p className="text-xs text-slate-500">
                  Bestellvorschlag wenn darunter
                </p>
              </div>
              <div className="space-y-2">
                <Label>Zielbestand (Stück)</Label>
                <Input
                  type="number"
                  min="0"
                  value={targetStock}
                  onChange={(e) => setTargetStock(e.target.value)}
                  placeholder="50"
                  data-testid="product-target-stock-input"
                />
                <p className="text-xs text-slate-500">
                  Menge bei Nachbestellung
                </p>
              </div>
            </div>
          </div>

          {/* Verkaufspreis Vorschau */}
          <div className="bg-emerald-50 rounded-md p-4">
            <h4 className="font-medium text-emerald-700 flex items-center gap-2 mb-2">
              <Euro className="w-4 h-4" />
              Berechneter Verkaufspreis
            </h4>
            <div className="text-3xl font-heading font-bold text-emerald-600">
              {calcSalePrice().toFixed(2)}€
            </div>
            <p className="text-xs text-emerald-600 mt-1">
              = EK Brutto ({calcPurchasePriceGross().toFixed(2)}€) + {markupPercent || 0}% Aufschlag
              {parseFloat(deposit) > 0 && ` + ${parseFloat(deposit).toFixed(2)}€ Pfand`}
            </p>
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex gap-3 pt-2 border-t">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onClose}
          className="flex-1"
        >
          Abbrechen
        </Button>
        <Button 
          type="submit" 
          disabled={saving}
          className="flex-1 bg-[#DC2626] hover:bg-[#B91C1C]"
          data-testid="product-save-btn"
        >
          {saving ? "Speichern..." : product ? "Aktualisieren" : "Erstellen"}
        </Button>
      </div>
      
      {/* Barcode Scanner Dialog */}
      <BarcodeScanner
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleBarcodeScan}
        title="Produkt-Barcode scannen"
      />
    </form>
  );
};

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [deleteProduct, setDeleteProduct] = useState(null);
  const [supplierPricesProduct, setSupplierPricesProduct] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/suppliers`)
      ]);
      setProducts(productsRes.data);
      setSuppliers(suppliersRes.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Produkte");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Produkte");
    }
  };

  const handleDelete = async () => {
    if (!deleteProduct) return;
    try {
      await axios.delete(`${API}/products/${deleteProduct.id}`);
      toast.success("Produkt gelöscht");
      fetchProducts();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    } finally {
      setDeleteProduct(null);
    }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.category?.toLowerCase().includes(search.toLowerCase()) ||
    p.ean?.toLowerCase().includes(search.toLowerCase())
  );

  const handleBarcodeScan = async (code) => {
    // Try to find product by EAN
    try {
      const res = await axios.get(`${API}/products/by-ean/${code}`);
      if (res.data) {
        setSearch(code);
        toast.success(`Produkt gefunden: ${res.data.name}`);
      }
    } catch (err) {
      // Product not found - ask if user wants to create new
      toast.error(`Kein Produkt mit EAN ${code} gefunden`);
      setSearch(code);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#DC2626]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="products-page">
      {/* Supplier Prices Dialog */}
      <SupplierPricesDialog
        product={supplierPricesProduct}
        suppliers={suppliers}
        onClose={() => setSupplierPricesProduct(null)}
      />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
            Produkte
          </h1>
          <p className="text-slate-500 mt-1">
            {products.length} Produkte im Sortiment
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#DC2626] hover:bg-[#B91C1C] btn-hover"
              onClick={() => setEditProduct(null)}
              data-testid="add-product-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Neues Produkt
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editProduct ? "Produkt bearbeiten" : "Neues Produkt"}
              </DialogTitle>
            </DialogHeader>
            <ProductForm 
              product={editProduct}
              onSave={fetchProducts}
              onClose={() => setDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="border-slate-200">
        <CardContent className="py-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Produkte suchen (Name, Kategorie, EAN)..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="product-search-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setScannerOpen(true)}
              data-testid="product-scan-search-btn"
              title="Barcode scannen"
            >
              <ScanBarcode className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Barcode Scanner Dialog */}
      <BarcodeScanner
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleBarcodeScan}
        title="Produkt per Barcode suchen"
      />

      {/* Products Grid */}
      {filteredProducts.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <AnimatePresence mode="popLayout">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onEdit={(p) => {
                  setEditProduct(p);
                  setDialogOpen(true);
                }}
                onDelete={setDeleteProduct}
                onSupplierPrices={setSupplierPricesProduct}
              />
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">
              {search ? "Keine Produkte gefunden" : "Noch keine Produkte angelegt"}
            </p>
            {!search && (
              <Button 
                className="mt-4 bg-[#DC2626] hover:bg-[#B91C1C]"
                onClick={() => {
                  setEditProduct(null);
                  setDialogOpen(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Erstes Produkt anlegen
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteProduct} onOpenChange={() => setDeleteProduct(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Produkt löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie "{deleteProduct?.name}" wirklich löschen? 
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-[#DC2626] hover:bg-[#B91C1C]"
              data-testid="confirm-delete-btn"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductsPage;
